import React from 'react';
import { gql } from '@soundxyz/gql-string';
import { ListContent } from '@components/Feed/ListViews/components/ListContent';
import { ListMinimalUser } from '@components/Feed/ListViews/components/ListMinimalUser';
import { ReleaseInfo } from '@components/Feed/ListViews/components/ReleaseInfo';
import { ReleasePill } from '@components/Feed/ListViews/components/ReleasePill';
import { ToggledAvatar } from '@components/Feed/ListViews/components/ToggledAvatar';
import {
  FeedListViewContainer,
  FeedListViewCoverContainer,
  FeedListViewDisplayWrapper,
  FeedListViewSubText,
  FeedListViewWrapper,
} from '@components/Feed/ListViews/components/shared';
import { ReleasePlayButton } from '@components/basics/ReleasePlayButton';
import { MediaQuery } from '@context/mediaQuery';
import type { FragmentType } from '@graphql/reactQuery';
import {
  BondingCurveSoldListFragmentDoc,
  getFragment,
  MinimalUserFragmentDoc,
  ReleasePillFragmentDoc,
} from '@graphql/reactQuery';
import { SPACE } from '@utils/constants';
import { formatWeiToEthDisplayFree } from '@utils/helpers';
import { Ordinal } from './components/Oridinal';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment BondingCurveSoldList on BondingCurveSold {
    id
    user {
      id
      avatar {
        id
        url
      }
      ...MinimalUser
    }
    release {
      id
      ...ReleasePill
    }
    paymentInWei
    quantity
    firstTokenId
    date
  }
`);

export const BondingCurveSoldList = ({
  activityFrag,
  releaseFeed = false,
}: {
  activityFrag: FragmentType<BondingCurveSoldListFragmentDoc>;
  releaseFeed?: boolean;
}) => {
  const { tabletLandscape, tablet, laptop, desktop } = MediaQuery.useContainer();
  const forceMobileView = releaseFeed && laptop && !desktop;

  const { release, date, user, quantity, paymentInWei, firstTokenId } = getFragment(
    BondingCurveSoldListFragmentDoc,
    activityFrag,
  );

  const {
    avatar,
    username,
    publicAddress,
    webappUri: userWebappUri,
  } = getFragment(MinimalUserFragmentDoc, user);

  const price = formatWeiToEthDisplayFree({ weiValue: paymentInWei, maxDecimals: 4 });
  const releaseData = getFragment(ReleasePillFragmentDoc, release);
  const { title, artist, webappUri: releaseWebappUri } = releaseData;

  return (
    <FeedListViewContainer>
      {!releaseFeed && (
        <FeedListViewCoverContainer>
          <ReleasePlayButton playButtonSize={tablet ? 'm' : 's'} releaseFrag={releaseData} />
        </FeedListViewCoverContainer>
      )}
      {releaseFeed && (
        <ToggledAvatar
          src={avatar?.url}
          username={username}
          walletAddress={publicAddress}
          userWebappUri={userWebappUri}
          forceMobileView={forceMobileView}
        />
      )}

      <ListContent isoDateString={date} truncateChildren forceMobileView={forceMobileView}>
        <FeedListViewWrapper margin="first">
          <ListMinimalUser userFrag={user} forceMobileView={forceMobileView} />
        </FeedListViewWrapper>
        <FeedListViewSubText>
          sold
          {SPACE}
        </FeedListViewSubText>
        {quantity === 1 && <Ordinal edition={firstTokenId} />}
        {quantity > 1 && (
          <FeedListViewSubText>
            {quantity} editions{SPACE}
          </FeedListViewSubText>
        )}
        {!releaseFeed && (
          <>
            <FeedListViewSubText>of{SPACE}</FeedListViewSubText>
            <FeedListViewDisplayWrapper media="tabletLandscape">
              <FeedListViewWrapper>
                <ReleasePill release={release} />
              </FeedListViewWrapper>
            </FeedListViewDisplayWrapper>

            {!tabletLandscape && !!artist.name && (
              <>
                <ReleaseInfo
                  releaseTitle={title}
                  releaseUri={releaseWebappUri}
                  artist={{
                    artistName: artist.name,
                    artistUri: artist.webappUri,
                  }}
                />
                <span>{SPACE}</span>
              </>
            )}
          </>
        )}

        <FeedListViewSubText>for{SPACE}</FeedListViewSubText>
        <FeedListViewSubText bold>{price}</FeedListViewSubText>
      </ListContent>
    </FeedListViewContainer>
  );
};
